import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  GastoComun,
  CargoUnidad,
  EmitirGastoComunRequest,
  EmitirGastoComunResponse,
  ActualizarBorradorRequest,
  AnularGastoComunRequest,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

interface GastosListResponse {
  items: GastoComun[];
}

interface GastoDetailResponse {
  gastoComun: GastoComun;
  cargos: CargoUnidad[];
}

export function useGastosComunes(condoId: string) {
  return useQuery({
    queryKey: queryKeys.gastosComunes(condoId),
    queryFn: () => api.get<GastosListResponse>(ApiRoutes.gastosComunes(condoId)),
    enabled: !!condoId,
  });
}

export function useGastoComun(condoId: string, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gastoComun(condoId, id ?? ''),
    queryFn: () =>
      api.get<GastoDetailResponse>(ApiRoutes.gastoComunById(condoId, id!)),
    enabled: !!condoId && !!id,
  });
}

/** POST /gastos-comunes — creates borrador (no cargos generated). */
export function useCrearBorrador(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmitirGastoComunRequest) =>
      api.post<{ gastoId: string; estado: 'borrador' }, EmitirGastoComunRequest>(
        ApiRoutes.gastosComunes(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
    },
  });
}

/** PATCH /gastos-comunes/{gastoId} — updates borrador lineas/vencimiento. */
export function useActualizarBorrador(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gastoId, ...body }: ActualizarBorradorRequest & { gastoId: string }) =>
      api.patch<{ gastoComun: GastoComun }, ActualizarBorradorRequest>(
        ApiRoutes.gastoComunById(condoId, gastoId),
        body,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.gastoComun(condoId, variables.gastoId),
      });
    },
  });
}

/** POST /gastos-comunes/{gastoId}/emitir — transitions borrador to emitido. */
export function useEmitirBorrador(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gastoId: string) =>
      api.post<EmitirGastoComunResponse>(
        ApiRoutes.gastoComunEmitir(condoId, gastoId),
      ),
    onSuccess: (_data, gastoId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.gastoComun(condoId, gastoId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard(condoId) });
    },
  });
}

/** DELETE /gastos-comunes/{gastoId} — discards a borrador permanently. */
export function useDescartarBorrador(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gastoId: string) =>
      api.delete(ApiRoutes.gastoComunById(condoId, gastoId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
    },
  });
}

/** POST /gastos-comunes/{gastoId}/anular — anula emitido/parcialmente_pagado. */
export function useAnularGasto(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gastoId, motivo }: { gastoId: string; motivo: string }) =>
      api.post<{ gastoComun: GastoComun }, AnularGastoComunRequest>(
        ApiRoutes.gastoComunAnular(condoId, gastoId),
        { motivo },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.gastoComun(condoId, variables.gastoId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard(condoId) });
    },
  });
}

/**
 * @deprecated Use useCrearBorrador + useEmitirBorrador for the new workflow.
 * Kept for backward compatibility during transition.
 */
export function useEmitirGastoComun(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmitirGastoComunRequest) =>
      api.post<EmitirGastoComunResponse, EmitirGastoComunRequest>(
        ApiRoutes.gastosComunes(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.gastosComunes(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard(condoId) });
    },
  });
}
